/**
 * Consola de administracion de usuarios -- antes solo editable por acceso
 * directo a la base de datos (README del backend). Institucional-solo,
 * tanto la lectura como cada escritura -- src/admin.ts en el backend.
 *
 * Sin flujo de invitacion por correo (no hay envio de correo en este
 * backend): el institucional fija una contraseña temporal al crear el
 * usuario, o la resetea despues, y la comunica por otro medio.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignUserToUnitApi,
  createUserApi,
  fetchAdminUsers,
  fetchInstitutionalDashboard,
  removeUserFromUnitApi,
  resetUserPasswordApi,
  updateUserRoles,
  type AdminUser,
  type UnitRole,
} from '../lib/queries';
import { PageHeader, QueryState } from '../components/ui';

const UNIT_ROLE_LABEL: Record<UnitRole, string> = {
  jefe: 'Jefe',
  colaborador: 'Colaborador',
  consulta: 'Consulta',
};

export function UsersAdmin() {
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ['admin', 'users'], queryFn: fetchAdminUsers });
  const unitsQ = useQuery({ queryKey: ['dash', 'inst', 'units-for-admin'], queryFn: fetchInstitutionalDashboard });
  const units = (unitsQ.data?.por_unidad ?? []).map((u) => ({ id: u.unit_id, name: u.name }));

  const [err, setErr] = useState<string | null>(null);
  const [openUser, setOpenUser] = useState<string | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin', 'users'] });

  const rolesMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof updateUserRoles>[1] }) => updateUserRoles(id, patch),
    onSuccess: invalidate,
    onError: (e) => setErr(e instanceof Error ? e.message : 'No se pudo cambiar el rol.'),
  });

  const assignMut = useMutation({
    mutationFn: ({ userId, unitId, role }: { userId: string; unitId: string; role: UnitRole }) => assignUserToUnitApi(userId, unitId, role),
    onSuccess: invalidate,
    onError: (e) => setErr(e instanceof Error ? e.message : 'No se pudo asignar la unidad.'),
  });

  const removeMut = useMutation({
    mutationFn: ({ userId, unitId }: { userId: string; unitId: string }) => removeUserFromUnitApi(userId, unitId),
    onSuccess: invalidate,
    onError: (e) => setErr(e instanceof Error ? e.message : 'No se pudo quitar la unidad.'),
  });

  const resetMut = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => resetUserPasswordApi(id, password),
    onError: (e) => setErr(e instanceof Error ? e.message : 'No se pudo resetear la contraseña.'),
  });

  const createMut = useMutation({
    mutationFn: createUserApi,
    onSuccess: invalidate,
    onError: (e) => setErr(e instanceof Error ? e.message : 'No se pudo crear el usuario.'),
  });

  const busy = rolesMut.isPending || assignMut.isPending || removeMut.isPending || resetMut.isPending || createMut.isPending;

  return (
    <>
      <PageHeader
        eyebrow="Configuración institucional"
        title="Usuarios"
        sub="Cuentas, roles y asignación a unidades. Sin invitación por correo: fije una contraseña temporal y comuníquela por otro medio."
      />

      <QueryState isLoading={usersQ.isLoading} error={usersQ.error}>
        {err && (
          <div className="callout callout--crit" style={{ marginBottom: 12 }}>
            {err}
          </div>
        )}

        <NewUserForm
          busy={busy}
          onCreate={(input) => {
            setErr(null);
            createMut.mutate(input);
          }}
        />

        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {(usersQ.data ?? []).map((u) => (
            <UserRow
              key={u.id}
              u={u}
              units={units}
              open={openUser === u.id}
              onToggleOpen={() => setOpenUser(openUser === u.id ? null : u.id)}
              busy={busy}
              onToggleInstitutional={() => {
                setErr(null);
                rolesMut.mutate({ id: u.id, patch: { is_institutional: !u.is_institutional } });
              }}
              onToggleAuditor={() => {
                setErr(null);
                rolesMut.mutate({ id: u.id, patch: { is_auditor: !u.is_auditor } });
              }}
              onAssign={(unitId, role) => {
                setErr(null);
                assignMut.mutate({ userId: u.id, unitId, role });
              }}
              onRemove={(unitId) => {
                setErr(null);
                removeMut.mutate({ userId: u.id, unitId });
              }}
              onResetPassword={(password) => {
                setErr(null);
                resetMut.mutate({ id: u.id, password });
              }}
              resetDone={resetMut.isSuccess && resetMut.variables?.id === u.id}
            />
          ))}
          {(usersQ.data ?? []).length === 0 && <p className="muted">Sin usuarios.</p>}
        </div>
      </QueryState>
    </>
  );
}

function UserRow({
  u,
  units,
  open,
  onToggleOpen,
  busy,
  onToggleInstitutional,
  onToggleAuditor,
  onAssign,
  onRemove,
  onResetPassword,
  resetDone,
}: {
  u: AdminUser;
  units: Array<{ id: string; name: string }>;
  open: boolean;
  onToggleOpen: () => void;
  busy: boolean;
  onToggleInstitutional: () => void;
  onToggleAuditor: () => void;
  onAssign: (unitId: string, role: UnitRole) => void;
  onRemove: (unitId: string) => void;
  onResetPassword: (password: string) => void;
  resetDone: boolean;
}) {
  const [newUnit, setNewUnit] = useState('');
  const [newRole, setNewRole] = useState<UnitRole>('colaborador');
  const [resetting, setResetting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const assignableUnits = units.filter((un) => !u.units.some((x) => x.unit_id === un.id));

  return (
    <div className="card">
      <div className="card__h" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          style={{ border: 0, background: 'transparent', cursor: 'pointer', flex: 1, textAlign: 'left' }}
          onClick={onToggleOpen}
        >
          <b style={{ fontSize: 14 }}>{u.email}</b>
        </button>
        {u.is_institutional && <span className="pill pill--accent">Institucional</span>}
        {u.is_auditor && <span className="pill pill--info">Auditor</span>}
        {u.mfa_enabled && <span className="pill pill--success">MFA</span>}
        <span className="muted" style={{ fontSize: 12 }}>
          {u.units.length} unidad{u.units.length === 1 ? '' : 'es'}
        </span>
        <button className="btn btn--ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={onToggleOpen}>
          {open ? 'Cerrar' : 'Detalle'}
        </button>
      </div>

      {open && (
        <div className="card__b" style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={u.is_institutional} disabled={busy} onChange={onToggleInstitutional} />
              Institucional (DPD / administración)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={u.is_auditor} disabled={busy} onChange={onToggleAuditor} />
              Auditor (lee todo, no crea ni edita)
            </label>
          </div>

          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              Unidades
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {u.units.map((ua) => (
                <div
                  key={ua.unit_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '5px 8px',
                  }}
                >
                  <span style={{ flex: 1 }}>{ua.unit_name}</span>
                  <span className="pill">{UNIT_ROLE_LABEL[ua.unit_role]}</span>
                  <button className="btn btn--ghost" style={{ fontSize: 11, padding: '2px 6px' }} disabled={busy} onClick={() => onRemove(ua.unit_id)}>
                    Quitar
                  </button>
                </div>
              ))}
              {u.units.length === 0 && <span className="muted" style={{ fontSize: 12 }}>Sin unidades asignadas.</span>}
            </div>
            {assignableUnits.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <select value={newUnit} onChange={(e) => setNewUnit(e.target.value)}>
                  <option value="">Unidad…</option>
                  {assignableUnits.map((un) => (
                    <option key={un.id} value={un.id}>
                      {un.name}
                    </option>
                  ))}
                </select>
                <select value={newRole} onChange={(e) => setNewRole(e.target.value as UnitRole)}>
                  {(Object.keys(UNIT_ROLE_LABEL) as UnitRole[]).map((r) => (
                    <option key={r} value={r}>
                      {UNIT_ROLE_LABEL[r]}
                    </option>
                  ))}
                </select>
                <button
                  className="btn"
                  disabled={!newUnit || busy}
                  onClick={() => {
                    onAssign(newUnit, newRole);
                    setNewUnit('');
                  }}
                >
                  Asignar
                </button>
              </div>
            )}
          </div>

          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              Contraseña
            </div>
            {resetting ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Contraseña temporal nueva (mín. 8 caracteres)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ flex: 1, minWidth: 220 }}
                />
                <button
                  className="btn btn--primary"
                  disabled={newPassword.length < 8 || busy}
                  onClick={() => {
                    onResetPassword(newPassword);
                    setNewPassword('');
                    setResetting(false);
                  }}
                >
                  Guardar
                </button>
                <button className="btn btn--ghost" onClick={() => setResetting(false)}>
                  Cancelar
                </button>
              </div>
            ) : (
              <button className="btn btn--ghost" onClick={() => setResetting(true)}>
                Resetear contraseña
              </button>
            )}
            {resetDone && !resetting && (
              <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Contraseña actualizada. Comuníquesela al usuario por otro medio.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NewUserForm({
  busy,
  onCreate,
}: {
  busy: boolean;
  onCreate: (input: { email: string; password: string; is_institutional?: boolean; is_auditor?: boolean }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [institutional, setInstitutional] = useState(false);
  const [auditor, setAuditor] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && password.length >= 8;

  if (!open) {
    return (
      <button className="btn btn--primary" onClick={() => setOpen(true)}>
        + Nuevo usuario
      </button>
    );
  }

  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'grid', gap: 8, maxWidth: 420 }}>
        <input placeholder="correo@utalca.cl" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input
          type="text"
          placeholder="Contraseña temporal (mín. 8 caracteres)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={institutional} onChange={(e) => setInstitutional(e.target.checked)} />
            Institucional
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={auditor} onChange={(e) => setAuditor(e.target.checked)} />
            Auditor
          </label>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className="btn btn--primary"
            disabled={!valid || busy}
            onClick={() => {
              onCreate({ email, password, is_institutional: institutional || undefined, is_auditor: auditor || undefined });
              setOpen(false);
              setEmail('');
              setPassword('');
              setInstitutional(false);
              setAuditor(false);
            }}
          >
            Crear usuario
          </button>
          <button className="btn btn--ghost" onClick={() => setOpen(false)}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
